import { useState } from 'react'
import { IconHeadset } from '@tabler/icons-react'
import { CSSTransition } from 'react-transition-group'

import { SelectOption } from 'components/ui/select'
import { useUser } from 'hooks/use-user'
import Text from 'components/ui/text'
import { Button } from 'components/ui/buttons'
import { Tabs } from 'components/ui/tabs'
import { getTierPrices } from 'utils/payment'
import { translate } from 'utils/translation'
import { Mixpanel } from 'utils/mixpanel'
import InquiryCustomPlanModal from 'components/modal/InquiryCustomPlanModal'
import ConfirmationModal from 'components/modal/ConfirmationModal'
import { usePlans } from 'hooks/use-plans'

import { Cards, PriceTable } from './components'
import {
  AdobeSVG,
  DellSVG,
  EbaySVG,
  MicrosoftSVG,
  NikeSVG,
  TwitterSVG,
} from './icons'
import NeilPigBankPng from './assets/neil-pig-bank.png'
import { combineData } from './utils'
import { Cards as CardsProps, TABS, title } from './consts'

const getData = (data: CardsProps, key: string) => {
  if (key === 'monthly' || key === 'lifetime') return data[key]
  return { items: [] }
}

interface NewPricingProps {
  onContinue: ({ tier, interval }: { tier: string; interval: string }) => void
}

const NewPricing = ({ onContinue }: NewPricingProps) => {
  const user = useUser()
  const tierPrices = getTierPrices(user.countryCode, true, 0)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [isContactModalSubmitted, setIsContactModalSubmitted] = useState(false)
  const [selected, setSelected] = useState(TABS[0])
  const { isLoading } = usePlans()
  // @ts-ignore TODO: Wait for TS component update
  const data = combineData(tierPrices)

  const onChange = (option: SelectOption) => {
    setSelected(option)
  }

  const selectedData = getData(data, selected.value)

  return (
    <>
      {isContactModalOpen && (
        <InquiryCustomPlanModal
          overlayStyle={{ zIndex: 999999 }}
          isOpen
          onClose={() => setIsContactModalOpen(false)}
          onSubmit={() => {
            Mixpanel.trackCustomPlanFormSubmitSuccess()
            setIsContactModalOpen(false)
            setIsContactModalSubmitted(true)
          }}
        />
      )}
      {isContactModalSubmitted && (
        // @ts-ignore TODO: Wait for TS component update
        <ConfirmationModal
          overlayStyle={{ zIndex: 999999 }}
          isOpen
          title={translate('help_form_success_heading')}
          message={translate('help_form_success_subheading')}
          renderFooter={() => {
            return (
              <Button
                className="tw-uppercase"
                onClick={() => setIsContactModalSubmitted(false)}
                variant="box"
              >
                {translate('close')}
              </Button>
            )
          }}
        />
      )}
      <div className="tw-flex tw-w-full tw-flex-col tw-bg-white tw-p-6 lg:tw-p-8 xl:tw-p-12">
        <Text
          as="h2"
          className="tw-m-0 tw-p-0 tw-text-center tw-text-3xl tw-font-bold tw-tracking-tight md:tw-text-4xl 2xl:tw-text-5xl"
        >
          {title}
        </Text>
        <Tabs
          options={TABS}
          selected={selected}
          onChange={onChange}
          className="tw-mx-auto tw-mt-4 tw-w-full tw-max-w-[480px]"
          mobileClassName="tw-mt-6"
          isFullWidth
          variant="control"
        />
        <CSSTransition
          in={selected.value === 'lifetime'}
          timeout={300}
          classNames="fade"
          unmountOnExit
        >
          <div className="tw-mt-8 tw-hidden tw-items-center tw-justify-between tw-gap-8 md:tw-flex lg:tw-gap-12">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Text className="tw-m-0 tw-text-2xl tw-font-bold lg:tw-text-3xl xl:tw-text-4xl">
                lifetime_saving
              </Text>
              <Text className="tw-m-0 tw-text-zinc-600 lg:tw-text-lg">
                lifetime_saving_subheading
              </Text>
            </div>
            <img src={NeilPigBankPng} alt="Neil Pig Bank" />
          </div>
        </CSSTransition>
        <div className="tw-flex tw-flex-col tw-gap-6 md:tw-gap-8">
          <div className="tw-mt-4 tw-flex tw-gap-4 tw-overflow-x-auto tw-overflow-y-hidden tw-py-3 md:tw-mt-8 xl:tw-gap-6">
            <Cards
              {...data[selected.value as keyof typeof data]}
              interval={selected.value}
              onContinue={onContinue}
              isLoading={isLoading}
            />
          </div>
          <Text
            className="tw-m-0 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-font-medium"
            renderChildren
          >
            {translate('custom_plan_cta_heading')}
            <Button
              variant="link"
              className="tw-capitalize tw-no-underline"
              onClick={() => setIsContactModalOpen(true)}
            >
              <Text
                className="tw-relative tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-medium after:tw-absolute after:tw-bottom-[-2px] after:tw-left-0 after:tw-h-px after:tw-w-full after:tw-bg-blue-500 after:tw-opacity-25 after:tw-content-['']"
                renderChildren
              >
                {translate('contact_sales')}
                <IconHeadset
                  width={20}
                  height={20}
                  className="tw-text-blue-500"
                />
              </Text>
            </Button>
          </Text>
          <div className="tw-rounded-xl tw-bg-zinc-50 tw-py-8">
            <Text className="tw-m-0 tw-text-center tw-text-2xl tw-font-bold">
              {translate('trusted_by', '250,000').replace(
                '<b>',
                '<b class="tw-text-orange-500 tw-font-[geomanistbold]">',
              )}
            </Text>
            <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-6">
              <AdobeSVG />
              <MicrosoftSVG />
              <NikeSVG />
              <TwitterSVG />
              <DellSVG />
              <EbaySVG />
            </div>
          </div>
          <div className="tw-mt-5 tw-overflow-auto tw-pt-3">
            <div className="tw-max-h-[90vh]">
              <PriceTable
                {...selectedData}
                items={selectedData.items}
                interval={selected.value}
                onContinue={onContinue}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default NewPricing
